import * as React from "react";
import { cn } from "../../../lib/utils";

export interface AppLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Sidebar component to render in the left column */
  sidebar: React.ReactNode;
  /** Main content area */
  children: React.ReactNode;
  /** Additional class name for the main content area */
  mainClassName?: string;
}

/**
 * AppLayout - Main application layout with sidebar and content area
 *
 * A flex-based layout that provides:
 * - Fixed sidebar slot on the left (80px width by default)
 * - Flexible main content area that fills remaining space
 * - Full viewport height with overflow handling
 *
 * @example
 * ```tsx
 * <AppLayout
 *   sidebar={<Sidebar {...sidebarProps} />}
 * >
 *   <YourPageContent />
 * </AppLayout>
 * ```
 */
function AppLayout({
  sidebar,
  children,
  className,
  mainClassName,
  ...props
}: AppLayoutProps) {
  return (
    <div
      className={cn("flex h-screen w-full flex-row overflow-hidden", className)}
      {...props}
    >
      {/* Sidebar */}
      {sidebar}

      {/* Main Content Area */}
      <main
        className={cn(
          "flex h-full min-h-0 flex-1 flex-col overflow-auto overscroll-contain [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
          mainClassName,
        )}
      >
        {children}
      </main>
    </div>
  );
}
AppLayout.displayName = "AppLayout";

export { AppLayout };
